// Leaflet 1.9.4 と同じ Web メルカトル計算 (CRS.EPSG3857 / Projection.SphericalMercator / Map.getBoundsZoom)。
// PDF のラスタライザ (pdf/render-map.ts) は Leaflet を使わずにここで表示範囲・タイル範囲・座標を決めるので、
// 画面の OverviewMiniMap と同じ切り取りになる。数式の出典は node_modules/leaflet/src。
import type { Point } from '@/lib/api.types';

const R = 6378137;
const MAX_LATITUDE = 85.0511287798;
const D = Math.PI / 180;
const A = 0.5 / (Math.PI * R);
const TILE_SIZE = 256;

export type XY = { x: number; y: number };
export type Size = { width: number; height: number };

export function scaleAt(zoom: number): number {
  return TILE_SIZE * 2 ** zoom;
}

/** 緯度経度 → zoom でのワールドピクセル座標 (Leaflet の Map.project)。 */
export function project(point: Point, zoom: number): XY {
  const lat = Math.max(Math.min(MAX_LATITUDE, point.lat), -MAX_LATITUDE);
  const sin = Math.sin(lat * D);
  const xm = R * point.lon * D;
  const ym = (R * Math.log((1 + sin) / (1 - sin))) / 2;
  const scale = scaleAt(zoom);
  return { x: scale * (A * xm + 0.5), y: scale * (-A * ym + 0.5) };
}

/** ワールドピクセル座標 → 緯度経度 (Leaflet の Map.unproject)。 */
export function unproject(px: XY, zoom: number): Point {
  const scale = scaleAt(zoom);
  const xm = (px.x / scale - 0.5) / A;
  const ym = -(px.y / scale - 0.5) / A;
  const lat = (2 * Math.atan(Math.exp(ym / R)) - Math.PI / 2) / D;
  return { lat, lon: xm / (R * D) };
}

export type View = { center: Point; zoom: number };

/**
 * Leaflet の fitBounds(bounds, {paddingTopLeft, paddingBottomRight, maxZoom}) と同じ中心・ズームを返す
 * (zoomSnap 1, minZoom 0, タイルの maxZoom 18)。
 */
export function getBoundsCenterZoom(
  points: Point[],
  size: Size,
  paddingTopLeft: [number, number],
  paddingBottomRight: [number, number],
  maxZoom: number,
  tileMaxZoom = 18,
): View {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLon = Math.min(minLon, p.lon);
    maxLon = Math.max(maxLon, p.lon);
  }
  const sw: Point = { lat: minLat, lon: minLon };
  const ne: Point = { lat: maxLat, lon: maxLon };
  const nw: Point = { lat: maxLat, lon: minLon };
  const se: Point = { lat: minLat, lon: maxLon };

  // getBoundsZoom: 余白を除いたサイズに bounds が収まる最大のズーム (0.01 に丸めて floor)。
  const padX = paddingTopLeft[0] + paddingBottomRight[0];
  const padY = paddingTopLeft[1] + paddingBottomRight[1];
  const innerW = size.width - padX;
  const innerH = size.height - padY;
  const z0 = 0;
  const nwPx = project(nw, z0);
  const sePx = project(se, z0);
  const boundsW = Math.abs(sePx.x - nwPx.x);
  const boundsH = Math.abs(sePx.y - nwPx.y);
  const scaleX = innerW / boundsW;
  const scaleY = innerH / boundsH;
  const scale = Math.min(scaleX, scaleY); // 0 除算は Infinity → 下の clamp で吸収
  let zoom = z0 + Math.log(scale) / Math.LN2;
  if (Number.isNaN(zoom)) zoom = Infinity;
  zoom = Math.round(zoom / 0.01) * 0.01;
  zoom = Math.floor(zoom);
  zoom = Math.max(0, Math.min(tileMaxZoom, zoom));
  zoom = Math.min(maxZoom, zoom);

  // _getBoundsCenterZoom: 余白の差分だけ中心をずらす。
  const paddingOffset: XY = {
    x: (paddingBottomRight[0] - paddingTopLeft[0]) / 2,
    y: (paddingBottomRight[1] - paddingTopLeft[1]) / 2,
  };
  const swPx = project(sw, zoom);
  const nePx = project(ne, zoom);
  const center = unproject(
    { x: (swPx.x + nePx.x) / 2 + paddingOffset.x, y: (swPx.y + nePx.y) / 2 + paddingOffset.y },
    zoom,
  );
  return { center, zoom };
}

/** 表示範囲の左上ワールドピクセル (Leaflet の pixelOrigin。整数丸め)。 */
export function pixelOrigin(view: View, size: Size): XY {
  const c = project(view.center, view.zoom);
  return { x: Math.round(c.x - size.width / 2), y: Math.round(c.y - size.height / 2) };
}

/** 緯度経度 → コンテナ内ピクセル (Leaflet の latLngToContainerPoint。整数丸め)。 */
export function toContainerPoint(point: Point, view: View, origin: XY): XY {
  const p = project(point, view.zoom);
  return { x: Math.round(p.x) - origin.x, y: Math.round(p.y) - origin.y };
}

export type TileRange = {
  tileZoom: number;
  /** 描画時の拡大率 2^(zoom - tileZoom)。 */
  scale: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

/** GridLayer._update と同じタイル範囲 (tileZoom はネイティブ上限でクランプ済みのもの)。 */
export function tileRange(view: View, size: Size, tileZoom: number): TileRange {
  const s = 2 ** (view.zoom - tileZoom);
  const c = project(view.center, tileZoom);
  const pixelCenter = { x: Math.floor(c.x), y: Math.floor(c.y) };
  const halfW = size.width / (2 * s);
  const halfH = size.height / (2 * s);
  const maxIndex = 2 ** tileZoom - 1;
  return {
    tileZoom,
    scale: s,
    minX: Math.floor((pixelCenter.x - halfW) / TILE_SIZE),
    maxX: Math.ceil((pixelCenter.x + halfW) / TILE_SIZE) - 1,
    minY: Math.max(0, Math.floor((pixelCenter.y - halfH) / TILE_SIZE)),
    maxY: Math.min(maxIndex, Math.ceil((pixelCenter.y + halfH) / TILE_SIZE) - 1),
  };
}

export function tileUrlFor(template: string, z: number, x: number, y: number): string {
  const n = 2 ** z;
  const wrappedX = ((x % n) + n) % n;
  return template.replace('{z}', String(z)).replace('{x}', String(wrappedX)).replace('{y}', String(y));
}

export const TILE_PX = TILE_SIZE;
